import { useRouter } from 'next/router'
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { WEBSITE_DEFAULT_LOCALE, WebsiteLocale } from '@/types/locales'

import { getPathWithoutAnyLocale, hasAnyLocale } from '@/lib/utils/localisationUtils'

interface LocaleContextType {
  currentLocale: WebsiteLocale
  setLocale: (locale: WebsiteLocale) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

const LOCALE_COOKIE_KEY = 'cPreferredLocale'

export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter()
  const [cookies, setCookie] = useCookies([LOCALE_COOKIE_KEY])
  const [currentLocale, setCurrentLocale] = useState<WebsiteLocale>(WEBSITE_DEFAULT_LOCALE)

  const setLocaleCookie = (locale: WebsiteLocale) => {
    try {
      setCookie(LOCALE_COOKIE_KEY, locale, {
        path: '/',
        maxAge: 30 * 24 * 60 * 60, // 30 days
        sameSite: 'lax',
      })
    } catch (error) {
      console.error('Error setting cookie:', error)
    }
  }

  // Set initial locale from URL when router is ready, or use stored preference
  useEffect(() => {
    if (router.isReady) {
      const localeFromUrl = router.query.locale as WebsiteLocale
      if (localeFromUrl) {
        setCurrentLocale(localeFromUrl)
        setLocaleCookie(localeFromUrl)
      } else {
        // Use stored preference if no locale in URL
        const stored = cookies[LOCALE_COOKIE_KEY]
        if (stored === 'en-gb' || stored === 'en-us') {
          setCurrentLocale(stored)
        }
      }
    }
  }, [router.isReady, router.query.locale, cookies, setCookie])

  const setLocale = (newLocale: WebsiteLocale) => {
    if (newLocale !== currentLocale && router.isReady) {
      setCurrentLocale(newLocale)
      setLocaleCookie(newLocale)

      // Only update URL if we're on a localized path
      if (hasAnyLocale(router.asPath)) {
        // Remove any existing locale and add the new one
        const pathWithoutLocale = getPathWithoutAnyLocale(router.asPath)
        router.push(`/${newLocale}${pathWithoutLocale}`)
      }
    }
  }

  return <LocaleContext.Provider value={{ currentLocale, setLocale }}>{children}</LocaleContext.Provider>
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}
